import {
  confirmEnvelopeSent,
  createEnvelope,
  getSenderViewUrl,
  type CreateEnvelopeRequest,
  type SignatureEnvelope,
} from '@commenda/api/docusign';
import { addFilesToServiceRequest } from '@commenda/api/service-request';
import { readErrorMessage } from '@commenda/api/workflow-template/helpers';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FileUploadStatus,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNotification } from '../../../hooks/useNotification';
import { SelectDocumentStep } from './SelectDocumentStep';
import { SelectRecipientsStep } from './SelectRecipientsStep';
import { SenderViewStep } from './SenderViewStep';
import {
  GENERIC_ERROR,
  recipientsFormSchema,
  type RecipientValidated,
  type RecipientsFormValues,
  type RequestSignatureModalProps,
  type TAgentToolMode,
  type TRequestSignatureModalStep,
  type TServiceRequestMode,
  type TWorkflowTaskMode,
} from './types';

/** Both matter-backed modes: same document picker, same upload-into-the-matter path. */
type TMatterProps = TServiceRequestMode | TWorkflowTaskMode;

export function RequestSignatureModal(props: RequestSignatureModalProps) {
  const { isOpen, onClose, mode } = props;
  const isMatterMode = mode === 'service-request' || mode === 'workflow-task';
  const workflowTask =
    mode === 'workflow-task' ? (props as TWorkflowTaskMode) : null;

  const { setNotification } = useNotification();
  const [step, setStep] =
    useState<TRequestSignatureModalStep>('select-document');
  const recipientsForm = useForm<RecipientsFormValues>({
    resolver: zodResolver(recipientsFormSchema),
    defaultValues: { recipients: [{ name: '', email: '' }] },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [envelope, setEnvelope] = useState<SignatureEnvelope | null>(null);
  const [senderViewUrl, setSenderViewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [uploadedFileIdForServiceRequest, setUploadedFileIdForServiceRequest] =
    useState<number | null>(null);
  const [
    pendingUploadedFileForServiceRequest,
    setPendingUploadedFileForServiceRequest,
  ] = useState<FileBasic | null>(null);
  const [isAddingFileToServiceRequest, setIsAddingFileToServiceRequest] =
    useState(false);
  const [uploadedFile, setUploadedFile] = useState<FileBasic | null>(null);

  const wasModalOpen = useRef(isOpen);

  useEffect(() => {
    if (wasModalOpen.current && !isOpen) {
      setStep('select-document');
      setSelectedFileIds([]);
      setUploadedFileIdForServiceRequest(null);
      setPendingUploadedFileForServiceRequest(null);
      recipientsForm.reset({ recipients: [{ name: '', email: '' }] });
      setEnvelope(null);
      setSenderViewUrl(null);
      setIsLoading(false);
      setUploadedFile(null);
      setIsUploading(false);
    }
    wasModalOpen.current = isOpen;
  }, [isOpen, recipientsForm]);

  useEffect(() => {
    if (!isOpen || workflowTask?.presetFileId == null) return;
    setSelectedFileIds([workflowTask.presetFileId]);
  }, [isOpen, workflowTask?.presetFileId]);

  const uploadedFilePayload = useMemo(
    () =>
      uploadedFile?.fileUrl && uploadedFile?.fileName
        ? [{ fileName: uploadedFile.fileName, fileUrl: uploadedFile.fileUrl }]
        : [],
    [uploadedFile]
  );

  const serviceFiles = isMatterMode
    ? (props.serviceRequest?.outputFiles?.filter(
        (f) => f.fileName && f.fileUrl
      ) ?? [])
    : [];

  const canProceedToRecipients = isMatterMode
    ? selectedFileIds.length > 0 ||
      uploadedFileIdForServiceRequest != null ||
      pendingUploadedFileForServiceRequest != null
    : uploadedFilePayload.length > 0;

  const showError = useCallback(
    (message: string) => {
      setNotification({ show: true, title: message, valid: false });
    },
    [setNotification]
  );

  const onSelectFile = useCallback(
    (f: FileBasic | null) => {
      if (!isMatterMode) {
        setUploadedFile(f);
        return;
      }
      const { companyId } = props as TServiceRequestMode;
      if (!f?.fileUrl || !companyId) {
        setUploadedFile(f);
        return;
      }
      setUploadedFile(f);
      setPendingUploadedFileForServiceRequest(f);
      setSelectedFileIds([]);
      setUploadedFileIdForServiceRequest(null);
    },
    [isMatterMode, props]
  );

  const onSelectFileIds = useCallback((ids: number[]) => {
    setUploadedFileIdForServiceRequest(null);
    setPendingUploadedFileForServiceRequest(null);
    setUploadedFile(null);
    setSelectedFileIds(ids);
  }, []);

  const onGoToRecipients = useCallback(async () => {
    if (!canProceedToRecipients) return;

    if (isMatterMode && pendingUploadedFileForServiceRequest) {
      const { serviceRequest, onFilesRefetch } = props as TServiceRequestMode;
      setIsAddingFileToServiceRequest(true);
      try {
        const result = await addFilesToServiceRequest(serviceRequest.id, {
          outputFiles: [
            {
              fileName: pendingUploadedFileForServiceRequest.fileName,
              fileUrl: pendingUploadedFileForServiceRequest.fileUrl,
              documentType: pendingUploadedFileForServiceRequest.documentType,
            },
          ],
        });
        const newFileId = result.success
          ? result.data?.fileIds?.[0]
          : undefined;
        if (newFileId != null) {
          setUploadedFileIdForServiceRequest(newFileId);
          setPendingUploadedFileForServiceRequest(null);
          setUploadedFile(null);
          onFilesRefetch?.();
          setStep('select-recipients');
        } else {
          showError('Failed to add document to service request.');
        }
      } catch {
        showError('Failed to add document to service request.');
      } finally {
        setIsAddingFileToServiceRequest(false);
      }
    } else {
      setStep('select-recipients');
    }
  }, [
    canProceedToRecipients,
    isMatterMode,
    pendingUploadedFileForServiceRequest,
    props,
    showError,
  ]);

  const createEnvelopeWithRecipients = useCallback(
    async (validRecipients: RecipientValidated[]) => {
      setIsLoading(true);
      let request: CreateEnvelopeRequest;
      let returnUrl: string;

      if (isMatterMode) {
        request = {
          serviceRequestId: (props as TMatterProps).serviceRequest.id,
          ...(workflowTask
            ? { serviceRequestTaskId: workflowTask.serviceRequestTaskId }
            : {}),
          fileIds:
            selectedFileIds.length > 0
              ? selectedFileIds
              : uploadedFileIdForServiceRequest != null
                ? [uploadedFileIdForServiceRequest]
                : [],
          recipients: validRecipients,
        };
        returnUrl = `${window.location.origin}/docusign/callback`;
      } else {
        request = {
          agentFirmId: (props as TAgentToolMode).agentFirmId,
          files: uploadedFilePayload,
          recipients: validRecipients,
        };
        returnUrl = `${window.location.origin}/tools/e-signature`;
      }

      try {
        const createRes = await createEnvelope(request);
        if (!createRes.ok) {
          if (createRes.status === 409) {
            const errorMessage =
              'This document is already out for signature. Void that request before sending it again.';
            showError(errorMessage);
            return;
          }
          showError((await readErrorMessage(createRes)) ?? GENERIC_ERROR);
          return;
        }

        const createdEnvelope = (await createRes.json()) as SignatureEnvelope;
        if (!createdEnvelope?.id) {
          showError(GENERIC_ERROR);
          return;
        }
        setEnvelope(createdEnvelope);

        const senderRes = await getSenderViewUrl(createdEnvelope.id, returnUrl);
        if (!senderRes.ok) {
          showError((await readErrorMessage(senderRes)) ?? GENERIC_ERROR);
          return;
        }

        const senderView = (await senderRes.json()) as { url?: string };
        const url = senderView?.url?.trim();
        if (!url) {
          showError(GENERIC_ERROR);
          return;
        }

        setSenderViewUrl(url);
        setStep('sender-view');
      } catch {
        showError(GENERIC_ERROR);
      } finally {
        setIsLoading(false);
      }
    },
    [
      isMatterMode,
      props,
      selectedFileIds,
      uploadedFileIdForServiceRequest,
      uploadedFilePayload,
      showError,
    ]
  );

  const handleRecipientsSubmit = useCallback(
    (data: RecipientsFormValues) => {
      createEnvelopeWithRecipients(
        data.recipients as unknown as RecipientValidated[]
      );
    },
    [createEnvelopeWithRecipients]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSenderViewEvent = useCallback(
    async (event: string) => {
      if (event !== 'send' || !envelope) return;

      try {
        const res = await confirmEnvelopeSent(envelope.id);
        if (!res.ok) {
          // Non-critical: status will be updated by webhook
        }
      } catch {
        // Non-critical: status will be updated by webhook
      }
      if (!isMatterMode) await (props as TAgentToolMode).onSuccess?.();
      else await workflowTask?.onSuccess?.();
      onClose();
    },
    [envelope, isMatterMode, props, onClose]
  );

  if (!isOpen) return null;

  const documentStepTitle = isMatterMode
    ? 'Select Document'
    : 'Request Signature';
  const documentStepDescription = isMatterMode
    ? 'Choose a document from this service to send for signature.'
    : 'Choose documents to send for signature.';

  const isSenderView = step === 'sender-view' && senderViewUrl != null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      {isSenderView ? (
        <SenderViewStep
          senderViewUrl={senderViewUrl!}
          onClose={handleClose}
          onEvent={handleSenderViewEvent}
        />
      ) : (
        <DialogContent className="max-w-lg">
          <DialogHeader className="flex flex-col items-start gap-1">
            <DialogTitle>
              {step === 'select-document'
                ? documentStepTitle
                : 'Select Recipients'}
            </DialogTitle>
            <DialogDescription>
              {step === 'select-document'
                ? documentStepDescription
                : 'Add the people who need to sign this document.'}
            </DialogDescription>
          </DialogHeader>

          <div
            className={`px-6 py-4 flex flex-col gap-4 overflow-y-auto ${
              step === 'select-recipients' ? 'max-h-[540px]' : 'max-h-[55vh]'
            }`}
          >
            {step === 'select-document' && (
              <SelectDocumentStep
                isServiceRequest={isMatterMode}
                serviceFiles={serviceFiles}
                selectedFileIds={selectedFileIds}
                companyId={(props as TMatterProps).companyId}
                uploadFile={uploadedFile}
                isUploading={isUploading}
                onSelectFileIds={onSelectFileIds}
                onFileSet={onSelectFile}
                onUploadStatusChange={(status) =>
                  setIsUploading(status === FileUploadStatus.UPLOADING)
                }
              />
            )}

            {step === 'select-recipients' && (
              <FormProvider {...recipientsForm}>
                <SelectRecipientsStep
                  companyId={
                    isMatterMode
                      ? ((props as TMatterProps).companyId ?? null)
                      : null
                  }
                />
              </FormProvider>
            )}
          </div>

          <DialogFooter className="flex justify-between">
            {step === 'select-recipients' ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setStep('select-document')}
                >
                  Back
                </Button>
                <Button
                  onClick={() =>
                    recipientsForm.handleSubmit(handleRecipientsSubmit)()
                  }
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating...' : 'Continue to field placement'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={onGoToRecipients}
                  disabled={
                    !canProceedToRecipients || isAddingFileToServiceRequest
                  }
                >
                  {isAddingFileToServiceRequest
                    ? 'Adding…'
                    : 'Next: Select Recipients'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
}
